import React from 'react';
import InteractiveField from '../common/InteractiveField';
import DateField from '../common/DateField';

/**
 * Компонент для отображения и редактирования "шапки" договора и информации о сторонах.
 * @param {object} data - Объект состояния, содержащий header и parties.
 * @param {function} onChange - Функция для обновления состояния в родительском компоненте.
 */
const HeaderAndPartiesSection = ({ data, onChange, showValidation = false }) => {
    // Безопасная деструктуризация данных с фолбэками
    const { header = {}, parties = {} } = data;

    /**
     * Обработчик изменений для полей в секции header.
     * @param {string} field - Название поля (например, 'city', 'date').
     * @param {string} value - Новое значение.
     */
    const handleHeaderChange = (field, value) => {
        // Создаем новый объект header и обновляем весь объект data
        const newHeader = { ...header, [field]: value };
        onChange({ ...data, header: newHeader });
    };

    /**
     * Обработчик изменений для полей в секции parties.
     * @param {string} field - Название поля (например, 'landlordName').
     * @param {string} value - Новое значение.
     */
    const handlePartiesChange = (field, value) => {
        const newParties = { ...parties, [field]: value };
        const updatedData = { ...data, parties: newParties };
        const signatures = data.signatures || { landlord: {}, tenant: {} };

        // Синхронизация ФИО из преамбулы в раздел "Адреса и реквизиты Сторон"
        if (field === 'landlordName') {
            updatedData.signatures = {
                ...signatures,
                landlord: { ...signatures.landlord, fullName: value }
            };
        }
        if (field === 'tenantName') {
            updatedData.signatures = {
                ...signatures,
                tenant: { ...signatures.tenant, fullName: value }
            };
        }

        // Синхронизация паспортных данных с разделом "Адреса и реквизиты Сторон"
        if (field === 'landlordPassportSerial' || field === 'landlordPassportNumber') {
            const sig = updatedData.signatures || signatures;
            updatedData.signatures = {
                ...signatures,
                landlord: {
                    ...sig.landlord,
                    passportSerial: field === 'landlordPassportSerial' ? value : newParties.landlordPassportSerial,
                    passportNumber: field === 'landlordPassportNumber' ? value : newParties.landlordPassportNumber
                }
            };
        }
        
        if (field === 'tenantPassportSerial' || field === 'tenantPassportNumber') {
            const sig = updatedData.signatures || signatures;
            updatedData.signatures = {
                ...sig,
                tenant: {
                    ...sig.tenant,
                    passportSerial: field === 'tenantPassportSerial' ? value : newParties.tenantPassportSerial,
                    passportNumber: field === 'tenantPassportNumber' ? value : newParties.tenantPassportNumber
                }
            };
        }

        onChange(updatedData);
    };

    return (
        <div className="document-preview-editor">
            {/* Блок с номером и названием договора */}
            <div className="document-title-block">
                <h3>
                    ДОГОВОР №
                    <InteractiveField
                        value={header.number}
                        onChange={(value) => handleHeaderChange('number', value)}
                        placeholder="5716"
                    />
                </h3>
                <p>найма жилого помещения (квартиры)</p>
            </div>

            {/* Блок с местом и датой подписания */}
            <div className="document-location-date">
                <InteractiveField
                    value={header.city}
                    onChange={(value) => handleHeaderChange('city', value)}
                    placeholder="г. Москва"
                    isRequired={true}
                    showValidation={showValidation}
                    validationFieldId="header-city"
                />
                <DateField
                    value={header.date}
                    onChange={(value) => handleHeaderChange('date', value)}
                    placeholder="01.01.2024"
                    isRequired={true}
                    showValidation={showValidation}
                />
            </div>

            {/* Основной параграф с информацией о сторонах */}
            <p className="document-preamble">
                Гражданин РФ
                <InteractiveField
                    value={parties.landlordName}
                    onChange={(value) => handlePartiesChange('landlordName', value)}
                    placeholder="Ф.И.О. полностью"
                    isRequired={true}
                    showValidation={showValidation}
                    validationFieldId="parties-landlordName"
                />,
                паспорт серии <InteractiveField
                    value={parties.landlordPassportSerial}
                    onChange={(value) => handlePartiesChange('landlordPassportSerial', value)}
                    placeholder="4512"
                    isRequired={false}
                />
                № <InteractiveField
                    value={parties.landlordPassportNumber}
                    onChange={(value) => handlePartiesChange('landlordPassportNumber', value)}
                    placeholder="654321"
                    isRequired={false}
                />,
                именуемый(ая) в дальнейшем "Наймодатель", с одной стороны и Гражданин РФ
                <InteractiveField
                    value={parties.tenantName}
                    onChange={(value) => handlePartiesChange('tenantName', value)}
                    placeholder="Ф.И.О. полностью"
                    isRequired={true}
                    showValidation={showValidation}
                    validationFieldId="parties-tenantName"
                />,
                паспорт серии <InteractiveField
                    value={parties.tenantPassportSerial}
                    onChange={(value) => handlePartiesChange('tenantPassportSerial', value)}
                    placeholder="4509"
                    isRequired={false}
                />
                № <InteractiveField
                    value={parties.tenantPassportNumber}
                    onChange={(value) => handlePartiesChange('tenantPassportNumber', value)}
                    placeholder="123456"
                    isRequired={false}
                />,
                именуемый(ая) в дальнейшем "Наниматель", с другой стороны, а совместно именуемые "Стороны", заключили настоящий договор (далее - Договор) о нижеследующем:
            </p>

            {/* Информационное сообщение о паспортных данных */}
            <p className="document-preamble" style={{ fontStyle: 'italic', color: 'var(--text-secondary)', background: 'var(--bg-subtle)', padding: '8px', borderRadius: '8px', marginBottom: '16px' }}>
                *Паспортные данные могут быть оставлены пустыми. Вы можете заполнить их позже после скачивания договора или непосредственно на бумаге.
            </p>
        </div>
    );
};

export default HeaderAndPartiesSection;